import { useState, useEffect, useMemo } from "react";
import { hasRangeValues } from "../utils";
import { FilterRange } from "../types";

interface UseRangeFilterProps {
  initialRange: FilterRange;
  hasFixedValue: boolean;
  onRangeChange: (range: FilterRange) => void;
  onFixedValueChange?: (hasFixed: boolean) => void;
}

/**
 * Custom hook to manage range filter state and logic
 */
export const useRangeFilter = ({
  initialRange,
  hasFixedValue,
  onRangeChange,
  onFixedValueChange,
}: UseRangeFilterProps) => {
  const [range, setRange] = useState<FilterRange>(initialRange);
  const [isFixed, setIsFixed] = useState<boolean>(hasFixedValue);

  // Update local state when props change
  useEffect(() => {
    setRange(initialRange);
    setIsFixed(hasFixedValue);
  }, [initialRange, hasFixedValue]);

  // Check if range has values
  const rangeHasValues = useMemo(() => hasRangeValues(range), [range]);

  // Handle range change
  const handleRangeChange = (newRange: FilterRange) => {
    setRange(newRange);
    onRangeChange(newRange);
  };

  // Handle fixed value change
  const handleFixedValueChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { checked } = event.target;

    // Only allow checked state if there's at least one range value
    if (!rangeHasValues && checked) {
      return;
    }

    setIsFixed(checked);
    if (onFixedValueChange) {
      onFixedValueChange(checked);
    }
  };

  // Handle clear filters
  const handleClearFilter = () => {
    const emptyRange = { min: undefined, max: undefined };
    setRange(emptyRange);
    setIsFixed(false);
    onRangeChange(emptyRange);
    if (onFixedValueChange) {
      onFixedValueChange(false);
    }
  };

  return {
    range,
    isFixed,
    rangeHasValues,
    handleRangeChange,
    handleFixedValueChange,
    handleClearFilter,
  };
};
